<template>
  <section class="container py-4">
    <div class="row">
      <div class="col-12 col-lg-3 mb-3">
        <div class="card">
          <div class="card-body p-2">
            <input
              v-model="search"
              type="search"
              class="form-control form-control-sm mb-2"
              placeholder="Search docs..."
              aria-label="Search docs"
            />
            <div class="list-group list-group-flush small">
              <router-link
                v-for="item in filteredItems"
                :key="item.slug"
                class="list-group-item list-group-item-action"
                :class="{ active: currentSlug === item.slug }"
                :to="{ path: '/docs/' + item.slug }"
              >
                {{ item.title }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 col-lg-9">
        <div class="card">
          <div class="card-body">
            <div v-if="loading" class="text-secondary">Loading…</div>
            <div v-else-if="error" class="text-danger">{{ error }}</div>
            <article v-else class="docs-content" v-html="html"></article>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { marked } from 'marked';

type DocIndexItem = { slug: string; file: string; title: string };

const route = useRoute();
const router = useRouter();

const items = ref<DocIndexItem[]>([]);
const search = ref('');
const currentSlug = ref<string>('');
const html = ref<string>('');
const loading = ref<boolean>(false);
const error = ref<string>('');

const filteredItems = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return items.value;
  return items.value.filter((i) => i.title.toLowerCase().includes(q) || i.slug.includes(q));
});

async function fetchIndex() {
  try {
    const res = await fetch('/docs/index.json', { cache: 'no-cache' });
    if (!res.ok) throw new Error(`Failed to load docs index (${res.status})`);
    items.value = await res.json();
  } catch (e: any) {
    error.value = e?.message || 'Unable to load docs index';
  }
}

async function loadDoc(slug?: string) {
  if (!items.value.length) return;
  loading.value = true;
  error.value = '';
  try {
    const targetSlug = slug || (items.value[0] && items.value[0].slug);
    const item = items.value.find((i) => i.slug === targetSlug) || items.value[0];
    if (!item) throw new Error('No docs available');
    currentSlug.value = item.slug;
    const res = await fetch(`/docs/${item.file}`, { cache: 'no-cache' });
    if (!res.ok) throw new Error(`Failed to load ${item.file} (${res.status})`);
    const md = await res.text();
    const rawHtml = marked.parse(md);
    // Rewrite relative markdown links (e.g., BRANDING.md#section) to our /docs/:slug routes
    const toSlug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    html.value = rawHtml.replace(/href=\"([^:\"]+?)\.md(#[^\"]*)?\"/g, (_m, base, hash) => {
      return `href=\"/docs/${toSlug(base)}${hash || ''}\"`;
    });
  } catch (e: any) {
    error.value = e?.message || 'Unable to load document';
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  await fetchIndex();
  const initialSlug = (route.params.slug as string) || '';
  await loadDoc(initialSlug);
});

watch(
  () => route.params.slug,
  async (slug) => {
    await loadDoc((slug as string) || '');
  }
);
</script>

<style scoped>
.docs-content :deep(h1),
.docs-content :deep(h2),
.docs-content :deep(h3) {
  margin-top: 1.2rem;
  font-weight: 700;
}
.docs-content :deep(pre) {
  background: rgba(0,0,0,0.05);
  border-radius: .25rem;
  padding: .75rem;
  overflow: auto;
}
.docs-content :deep(code) {
  background: rgba(0,0,0,0.05);
  border-radius: .25rem;
  padding: .1rem .25rem;
}
.docs-content :deep(a) { text-decoration: underline; }
</style>
